import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { Button } from "@/components/ui/button";
import { ArrowRight, BarChart3, TrendingUp, Brain, LineChart, PieChart, Activity, Zap, Clock, Target } from "lucide-react";
import DemoScheduler from "@/components/DemoScheduler";

const features = [
  {
    icon: BarChart3,
    title: "Dashboards em Tempo Real",
    description: "Visualize métricas críticas de atendimento, SLA, volume de tickets e performance da equipe em tempo real.",
  },
  {
    icon: Brain,
    title: "Previsões Inteligentes",
    description: "IA analisa padrões históricos para prever demanda futura, gargalos e necessidades de recursos.",
  },
  {
    icon: TrendingUp,
    title: "Análise de Tendências",
    description: "Identifique tendências emergentes, problemas recorrentes e oportunidades de melhoria automaticamente.",
  },
  {
    icon: Target,
    title: "KPIs Personalizados",
    description: "Crie e monitore KPIs específicos do seu negócio com alertas automáticos e recomendações.",
  },
];

const benefits = [
  {
    icon: Clock,
    title: "Redução de 40% no Tempo de Análise",
    description: "Automatize relatórios e análises que antes levavam horas. VIA gera insights acionáveis instantaneamente.",
  },
  {
    icon: Zap,
    title: "Decisões 3x Mais Rápidas",
    description: "Com dados em tempo real e previsões precisas, sua equipe toma decisões estratégicas com confiança.",
  },
  {
    icon: Activity,
    title: "Aumento de 60% na Eficiência",
    description: "Identifique e resolva problemas antes que impactem clientes. Otimize recursos proativamente.",
  },
];

const useCases = [
  {
    title: "Previsão de Demanda",
    description: "Antecipe picos de volume de chamados por período, sazonalidade ou eventos específicos. Dimensione sua equipe adequadamente.",
    metrics: ["Precisão de 92% nas previsões", "Redução de 35% em horas extras", "SLA mantido em 98%+"],
  },
  {
    title: "Análise de Performance",
    description: "Monitore performance individual e de equipe com métricas detalhadas. Identifique top performers e áreas de treinamento.",
    metrics: ["Visão 360° da equipe", "Identificação automática de gaps", "Recomendações de capacitação"],
  },
  {
    title: "Otimização de Processos",
    description: "Descubra gargalos, tempos de espera e etapas desnecessárias. VIA sugere otimizações baseadas em dados.",
    metrics: ["Redução de 45% no tempo médio", "25% menos retrabalho", "Satisfação do cliente +30%"],
  },
];

const AnalyticsPreditivo = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
        <SEO
            title="Analytics Preditivo para Service Desk e Operações"
            description="Antecipe picos de chamados, riscos de SLA e gargalos da operação com o analytics preditivo da Aroan. Dashboards em tempo real e alertas automáticos."
            canonical="https://aroan.com.br/analytics-preditivo"
            ogType="website"
            article={false}
        />
        <BreadcrumbSchema
            items={[
                { name: "Home", url: "https://aroan.com.br/" },
                { name: "Analytics Preditivo", url: "https://aroan.com.br/analytics-preditivo" },
            ]}
        />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-hero pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6 animate-fade-in">
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Analytics Preditivo</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in-delay-1">
              Transforme Dados em{" "}
              <span className="text-primary">Decisões Estratégicas</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto animate-fade-in-delay-2">
              Analytics preditivo alimentado por IA que não apenas mostra o que aconteceu, 
              mas prevê o que vai acontecer e recomenda ações específicas para otimizar sua operação.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-delay-3">
              <Button size="lg" className="group" onClick={() => setDemoOpen(true)}>
                Ver Demo do Analytics
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="group">
                Baixar Exemplo de Relatório
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Recursos Que Vão Além de{" "}
              <span className="text-primary">Dashboards Tradicionais</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Não é apenas visualização de dados - é inteligência artificial analisando, 
              prevendo e recomendando ações em tempo real.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="group p-8 rounded-2xl bg-card border border-border hover:shadow-card hover:border-primary/20 transition-all duration-300 animate-scale-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary/5 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Resultados <span className="text-primary">Mensuráveis e Comprovados</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Empresas que implementaram Analytics Preditivo da AROAN viram transformação real
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-8 rounded-2xl bg-card border border-border hover:shadow-lg transition-all duration-300 animate-scale-in text-center"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-xl bg-success/10 flex items-center justify-center mb-6 mx-auto">
                    <Icon className="w-8 h-8 text-success" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-success">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Casos de Uso <span className="text-primary">Práticos</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Veja como Analytics Preditivo resolve problemas reais do dia a dia
            </p>
          </div>

          <div className="space-y-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-card transition-all duration-300 animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <LineChart className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold mb-3">{useCase.title}</h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {useCase.description}
                      </p>
                      <div className="grid sm:grid-cols-3 gap-4">
                        {useCase.metrics.map((metric, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                            <span className="text-sm font-medium">{metric}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary/5 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Como Funciona a <span className="text-primary">Inteligência Preditiva</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              VIA analisa milhões de pontos de dados para entregar insights acionáveis
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {[
                {
                  step: "01",
                  title: "Coleta e Unificação",
                  description: "VIA coleta dados de todas as interações, tickets, formulários e canais automaticamente.",
                },
                {
                  step: "02",
                  title: "Análise e Aprendizado",
                  description: "Machine Learning identifica padrões, correlações e anomalias que humanos demorariam dias para descobrir.",
                },
                {
                  step: "03",
                  title: "Previsões e Recomendações",
                  description: "IA gera previsões precisas e recomenda ações específicas baseadas em dados históricos e contexto atual.",
                },
                {
                  step: "04",
                  title: "Melhoria Contínua",
                  description: "Sistema aprende com resultados das ações tomadas, refinando previsões e recomendações continuamente.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex gap-6 p-8 rounded-2xl bg-card border border-border hover:border-primary/20 transition-all duration-300 animate-scale-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                      <span className="text-2xl font-bold text-primary-foreground">{item.step}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-primary/10 to-primary/5 rounded-3xl p-12 border border-primary/20 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Pronto Para Transformar{" "}
              <span className="text-primary">Dados em Estratégia?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Veja o Analytics Preditivo em ação com uma demo personalizada de 30 minutos
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="group" onClick={() => setDemoOpen(true)}>
                Agendar Demo Gratuita
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="group">
                Falar com Especialista
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <DemoScheduler open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
};

export default AnalyticsPreditivo;
